"use client"

import { Fragment, useRef } from "react"
import { Col, ConfigProvider, Divider, Layout, Row, Typography } from "antd"
import PowerBy from "./PowerBy"
import PrivacePolicyAndTermsConditions from "./PrivacePolicyAndTermsConditions"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFacebook, faTiktok, faInstagramSquare } from "@fortawesome/free-brands-svg-icons"
import { useIsDesktop } from "@/hooks/app/useIsDesktop"
import ModalContact, { ModalContactRef } from "./ModalContact"
import { TypeStoreDetail } from "@/lib/redux/features/store/type"
import NextImage from "@/components/common/NextImage"
import Link from "@/components/common/Link"

const BasicLayoutFooter = ({ store }: { store: TypeStoreDetail }) => {
  const isDesktop = useIsDesktop()
  const refModalContact = useRef<ModalContactRef>(null)
  if (store.platform_name === 'crowdaa') {
    return <Fragment />
  }

  const imagesCard = [
    {
      image: "/static/cards/discover-card.png",
      alt: "Discover card"
    },
    {
      image: "/static/cards/visa-card.png",
      alt: "Visa card"
    },
    {
      image: "/static/cards/mclogo.png",
      alt: "Master card"
    },
    {
      image: "/static/cards/american-express-card.png",
      alt: "American Express Card"
    },
  ]
  return (
    <>
      <ModalContact ref={refModalContact} />
      <ConfigProvider
        theme={{
          token: {
            colorText: store.footer_text_color
          },
          components: {
            Menu: {
              colorBgContainer: store.footer_bg_color,
            }
          }
        }}
      >
        <Layout.Footer
          className='basic_layout--footer'
          style={{ zIndex: 10 }}
        >
          <Row justify="center">
            <Col className="page__section">
              {!isDesktop && !!store.supports.email && (
                <>
                  <Row>
                    <Typography.Text style={{ margin: 0, fontSize: '2rem', fontWeight: 'bold' }}>SUPPORT</Typography.Text>
                  </Row>
                  <Row style={{ marginBottom: '1rem' }}>
                    <Typography.Text>Contact <Typography.Link style={{ color: store.footer_text_color, textDecorationLine: 'underline' }} href={`mailto: ${store.supports.email}?subject=Inquiry`}>{store.supports.email}</Typography.Link> for help with an order.</Typography.Text>
                  </Row>
                </>
              )}

              <Row wrap={false} align="bottom" justify="space-between">
                <Col sm={{ flex: "auto" }} xs={{ flex: "10rem" }}>
                  {isDesktop && !!store.supports.email && (
                    <>
                      <Row>
                        <Typography.Text style={{ margin: 0, fontSize: '2rem', fontWeight: 'bold' }}>SUPPORT</Typography.Text>
                      </Row>
                      <Row style={{ marginBottom: '1rem' }}>
                        <Typography.Text>Contact <Typography.Link style={{ color: store.footer_text_color, textDecorationLine: 'underline' }} href={`mailto: ${store.supports.email}?subject=Inquiry`}>{store.supports.email}</Typography.Link> for help with an order.</Typography.Text>
                      </Row>
                    </>
                  )}
                  <Row align="middle" gutter={5}>
                    {imagesCard.map(({ image, alt }) => (
                      <Col key={image}>
                        <NextImage
                          hiddenSpin
                          alt={alt}
                          src={image}
                          height={25}
                          width={40}
                        />
                      </Col>
                    ))}
                  </Row>
                </Col>
                <Col>
                  <Row justify="center" wrap={false} style={{ maxWidth: '100%', marginInline: 0 }} gutter={5} align="bottom">
                    <Col>
                      {store.logo && (
                        <NextImage
                          hiddenSpin
                          height={35}
                          width={35}
                          alt={`logo store ${store.store_name}`}
                          src={store.logo}
                        />
                      )}
                    </Col>
                    <Divider orientation="vertical" />
                    <Col>
                      <Row justify='end'>
                        <Col>
                          {[
                            {
                              key: "about",
                              label: "About",
                              href: "/about",
                              hidden: store.about?.content === '<p></p>' || !store?.about?.content
                            },
                            {
                              key: "contact",
                              label: "Contact",
                              onClick: () => refModalContact.current?.open()
                            }
                          ]
                            .filter(route => !route.hidden)
                            .map((route) => {
                              const text = (
                                <Typography.Text style={{ textAlign: 'center' }}>
                                  {route.label}
                                </Typography.Text>
                              )
                              return (
                                <Row justify="center" onClick={route.onClick} key={route.key} style={{ cursor: 'pointer' }}>
                                  {!route.href
                                    ? text
                                    : (
                                      <Link href={route.href}>
                                        {text}
                                      </Link>
                                    )}
                                </Row>
                              )
                            })}
                        </Col>
                      </Row>
                      <Row align='middle' gutter={[5, 5]} wrap={false}>
                        {[
                          {
                            url: store.custom_url?.url,
                            icon: store.custom_url?.icon || store.logo,
                            type: 'custom_url'
                          },
                          {
                            url: store.facebook?.url,
                            icon: store.facebook?.icon || faFacebook,
                            type: 'facebook'
                          },
                          {
                            url: store.tiktok?.url,
                            icon: store.tiktok?.icon || faTiktok,
                            type: 'tiktok'
                          },
                          {
                            url: store.instagram?.url,
                            icon: store.instagram?.icon || faInstagramSquare,
                            type: 'instagram'
                          }
                        ].map(({ url, icon, type }, idx) => {
                          if (!url || !icon) {
                            return <Fragment key={idx} />
                          }
                          return (
                            <Col key={idx} style={{ marginTop: '0.5rem', lineHeight: 1 }}>
                              <Link target='_blank' href={url.includes('http') ? url : `https://${url}`}>
                                <Typography.Text style={{ textAlign: 'center', fontSize: '1.8rem' }}>
                                  {typeof icon === 'string' ? <NextImage hiddenSpin src={icon} height={40} width={40} alt={`${type} icon`} style={{ height: '3rem', objectFit: 'contain' }} /> : <FontAwesomeIcon style={{ fontSize: '3rem' }} icon={icon} />}
                                </Typography.Text>
                              </Link>
                            </Col>
                          )
                        })}
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Divider style={{ marginBlock: '0.5rem', borderColor: store.footer_text_color }} />
              <Row style={{ maxWidth: '100%' }} justify="space-between">
                <Col xs={24} sm={{ flex: "auto" }}>
                  <Row justify={{ xs: "center", sm: "start" }}>
                    <PowerBy store={store} />
                  </Row>
                </Col>
                <Col xs={24} sm={{ flex: "auto" }}>
                  <Row justify={{ 'sm': 'end', xs: 'center' }}>
                    <Col>
                      <PrivacePolicyAndTermsConditions store={store} />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
        </Layout.Footer>
      </ConfigProvider>
    </>

  )
}

export default BasicLayoutFooter